import { schemaRedefinirSenha, type EntradaRedefinirSenha } from '@baiocchi/shared'
import { Alerta, Botao, Campo, Entrada } from '@baiocchi/ui'
import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { autenticacaoApi } from '../../api/autenticacao'
import { ForcaSenha, senhaAceitavel } from '../../componentes/forca-senha'
import { useFormulario } from '../../ganchos/usar-formulario'
import { MolduraAutenticacao } from './moldura-autenticacao'

/**
 * TELA-00B — Redefinir senha por token.
 *
 * Serve os **dois** tokens: `RECUPERACAO` (esqueci minha senha) e `CONVITE`
 * (primeiro acesso de usuário novo). É o mesmo endpoint e a mesma tela; o que
 * muda é o texto, por `?convite=1` no link do e-mail — a substituição do envio de
 * senha em texto claro que a spec exige (TELA-11).
 *
 * Não há tentativa de "validar o token antes": a API só responde sobre ele no
 * consumo. Pedir a senha e só então descobrir que o link expirou é chato, mas uma
 * rota de checagem prévia daria a quem tem o link um oráculo de validade.
 */
export function PaginaRedefinirSenha() {
  const [parametros] = useSearchParams()
  const token = parametros.get('token') ?? ''
  const ehConvite = parametros.get('convite') === '1'
  const [concluido, setConcluido] = useState(false)

  const formulario = useFormulario<EntradaRedefinirSenha>({
    token,
    senha: '',
    confirmacaoSenha: '',
  })

  async function submeter(evento: React.FormEvent) {
    evento.preventDefault()
    const concluiu = await formulario.enviar(
      schemaRedefinirSenha,
      (valores) => ({ ...valores, token }),
      async (corpo) => {
        await autenticacaoApi.redefinirSenha(corpo)
        setConcluido(true)
      },
    )
    if (!concluiu) {
      formulario.definir('senha', '')
      formulario.definir('confirmacaoSenha', '')
    }
  }

  if (!token) {
    return (
      <MolduraAutenticacao titulo="Link inválido">
        <Alerta tom="perigo" titulo="Este link não é válido">
          <p>
            O endereço não traz o código de verificação. Isso acontece quando o link é copiado
            parcialmente do e-mail.
          </p>
        </Alerta>
        <Botao
          className="mt-5"
          larguraTotal
          tamanho="lg"
          onClick={() => {
            window.location.href = '/esqueci-minha-senha'
          }}
        >
          Solicitar um link novo
        </Botao>
      </MolduraAutenticacao>
    )
  }

  if (concluido) {
    return (
      <MolduraAutenticacao titulo="Senha definida">
        <Alerta tom="sucesso" titulo="Tudo pronto">
          <p>
            Sua senha foi definida e todas as sessões anteriores foram encerradas por segurança.
            Entre com a senha nova para continuar.
          </p>
        </Alerta>
        <Botao
          className="mt-5"
          larguraTotal
          tamanho="lg"
          onClick={() => {
            window.location.href = '/entrar'
          }}
        >
          Ir para o login
        </Botao>
      </MolduraAutenticacao>
    )
  }

  const podeEnviar =
    senhaAceitavel(formulario.valores.senha) &&
    formulario.valores.senha === formulario.valores.confirmacaoSenha

  return (
    <MolduraAutenticacao
      titulo={ehConvite ? 'Defina sua senha de acesso' : 'Criar nova senha'}
      descricao={
        ehConvite
          ? 'Seu acesso ao Baiocchi Leads foi criado. Escolha uma senha para entrar.'
          : 'Escolha uma senha nova. As sessões abertas serão encerradas.'
      }
      rodape={
        <p>
          <Link to="/entrar" className="font-medium text-marca-600 underline-offset-2 hover:underline">
            Voltar para o login
          </Link>
        </p>
      }
    >
      <form onSubmit={submeter} className="flex flex-col gap-4" noValidate>
        {formulario.erroGeral ? (
          <Alerta tom="perigo" titulo="Não foi possível redefinir">
            <p>{formulario.erroGeral}</p>
            <p className="mt-2">
              Links de recuperação valem uma única vez e expiram rápido.{' '}
              <Link to="/esqueci-minha-senha" className="font-medium text-marca-600 hover:underline">
                Solicitar um link novo
              </Link>
              .
            </p>
          </Alerta>
        ) : null}

        <Campo rotulo="Nova senha" obrigatorio erro={formulario.erros.senha}>
          {(atributos) => (
            <Entrada
              {...atributos}
              type="password"
              autoFocus
              autoComplete="new-password"
              value={formulario.valores.senha}
              temErro={atributos.temErro}
              onChange={(evento) => formulario.definir('senha', evento.target.value)}
            />
          )}
        </Campo>

        <ForcaSenha senha={formulario.valores.senha} />

        <Campo
          rotulo="Confirmar nova senha"
          obrigatorio
          erro={formulario.erros.confirmacaoSenha}
        >
          {(atributos) => (
            <Entrada
              {...atributos}
              type="password"
              autoComplete="new-password"
              value={formulario.valores.confirmacaoSenha}
              temErro={atributos.temErro}
              onChange={(evento) => formulario.definir('confirmacaoSenha', evento.target.value)}
            />
          )}
        </Campo>

        <Botao
          type="submit"
          larguraTotal
          tamanho="lg"
          carregando={formulario.enviando}
          disabled={!podeEnviar}
        >
          {ehConvite ? 'Definir senha e acessar' : 'Redefinir senha'}
        </Botao>
      </form>
    </MolduraAutenticacao>
  )
}
